import React, { useEffect, useState } from 'react';
import { Clock, MapPin, X, CheckCircle2, AlertCircle } from 'lucide-react';
import type { LoggedInUser } from './LoginScreen';
interface CheckInPopupProps {
  user: LoggedInUser;
  onClose: () => void;
}
// Mock shift data for the logged-in user
const SHIFT_SCHEDULE: Record<string, {
  shift: string;
  start: string;
  end: string;
  color: string;
}> = {
  rishi: {
    shift: 'Morning',
    start: '06:00 AM',
    end: '02:00 PM',
    color: 'green'
  },
  sanjay: {
    shift: 'Evening',
    start: '02:00 PM',
    end: '10:00 PM',
    color: 'blue'
  },
  priya: {
    shift: 'Morning',
    start: '06:00 AM',
    end: '02:00 PM',
    color: 'green'
  },
  amit: {
    shift: 'Night',
    start: '10:00 PM',
    end: '06:00 AM',
    color: 'purple'
  },
  neha: {
    shift: 'Morning',
    start: '06:00 AM',
    end: '02:00 PM',
    color: 'green'
  },
  rajesh: {
    shift: 'Evening',
    start: '02:00 PM',
    end: '10:00 PM',
    color: 'blue'
  },
  sunita: {
    shift: 'Morning',
    start: '06:00 AM',
    end: '02:00 PM',
    color: 'green'
  },
  manoj: {
    shift: 'Night',
    start: '10:00 PM',
    end: '06:00 AM',
    color: 'purple'
  },
  kavita: {
    shift: 'Morning',
    start: '06:00 AM',
    end: '02:00 PM',
    color: 'green'
  },
  deepak: {
    shift: 'Evening',
    start: '02:00 PM',
    end: '10:00 PM',
    color: 'blue'
  },
  anita: {
    shift: 'Morning',
    start: '06:00 AM',
    end: '02:00 PM',
    color: 'green'
  },
  vikram: {
    shift: 'Night',
    start: '10:00 PM',
    end: '06:00 AM',
    color: 'purple'
  }
};
const SHIFT_COLORS: Record<string, string> = {
  green: 'bg-green-100 text-green-700 border-green-200',
  blue: 'bg-blue-100 text-blue-700 border-blue-200',
  purple: 'bg-purple-100 text-purple-700 border-purple-200'
};
export function CheckInPopup({
  user,
  onClose
}: CheckInPopupProps) {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [checkedIn, setCheckedIn] = useState(false);
  const [checkInTime, setCheckInTime] = useState<Date | null>(null);
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);
  const schedule = SHIFT_SCHEDULE[user.name.toLowerCase()] || {
    shift: 'Morning',
    start: '06:00 AM',
    end: '02:00 PM',
    color: 'green'
  };
  const handleCheckIn = () => {
    setCheckedIn(true);
    setCheckInTime(new Date());
  };
  return <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[100] p-4" data-id="element-240">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-300" data-id="element-241">
        {/* Header */}
        <div className="bg-gradient-to-r from-orange-500 to-orange-600 px-6 py-5 flex items-center justify-between" data-id="element-242">
          <div className="flex items-center gap-3" data-id="element-243">
            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center" data-id="element-244">
              <Clock size={22} className="text-white" data-id="element-245" />
            </div>
            <div data-id="element-246">
              <h3 className="text-white font-bold text-lg" data-id="element-247">
                Attendance Check-In
              </h3>
              <p className="text-white/80 text-xs" data-id="element-248">
                Mark your attendance for today
              </p>
            </div>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white p-1 transition-colors" data-id="element-249">
            <X size={20} data-id="element-250" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-5" data-id="element-251">
          {/* User Info */}
          <div className="flex items-center gap-4" data-id="element-252">
            <div className="w-14 h-14 rounded-full bg-orange-100 border-2 border-orange-200 overflow-hidden" data-id="element-253">
              <img src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${user.seed}&backgroundColor=transparent`} alt={user.name} className="w-full h-full object-cover" data-id="element-254" />
            </div>
            <div data-id="element-255">
              <h4 className="font-bold text-gray-900 text-lg" data-id="element-256">{user.name}</h4>
              <p className="text-sm text-gray-500" data-id="element-257">{user.role}</p>
            </div>
          </div>

          {/* Shift Info */}
          <div className="bg-gray-50 rounded-xl p-4 border border-gray-100 space-y-3" data-id="element-258">
            <div className="flex justify-between items-center" data-id="element-259">
              <span className="text-sm text-gray-500" data-id="element-260">Today's Shift</span>
              <span className={`text-sm font-bold px-3 py-1 rounded-full border ${SHIFT_COLORS[schedule.color]}`} data-id="element-261">
                {schedule.shift}
              </span>
            </div>
            <div className="flex justify-between items-center" data-id="element-262">
              <span className="text-sm text-gray-500" data-id="element-263">Duty Timing</span>
              <span className="text-sm font-semibold text-gray-800" data-id="element-264">
                {schedule.start} — {schedule.end}
              </span>
            </div>
            <div className="flex justify-between items-center" data-id="element-265">
              <span className="text-sm text-gray-500" data-id="element-266">Current Time</span>
              <span className="text-sm font-bold text-gray-900 font-mono" data-id="element-267">
                {currentTime.toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit'
              })}
              </span>
            </div>
          </div>

          {/* Check In Action */}
          {!checkedIn ? <button onClick={handleCheckIn} className="w-full py-4 rounded-xl text-lg font-bold text-white bg-green-500 hover:bg-green-600 shadow-lg hover:shadow-green-200 transition-all hover:-translate-y-0.5 flex items-center justify-center gap-3" data-id="element-268">
              <MapPin size={22} data-id="element-269" />
              CHECK IN NOW
            </button> : <div className="space-y-3" data-id="element-270">
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3" data-id="element-271">
                <CheckCircle2 size={24} className="text-green-600" data-id="element-272" />
                <div data-id="element-273">
                  <p className="font-bold text-green-800" data-id="element-274">
                    Checked In Successfully!
                  </p>
                  <p className="text-sm text-green-600" data-id="element-275">
                    at{' '}
                    {checkInTime?.toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit'
                })}
                  </p>
                </div>
              </div>
              <button onClick={onClose} className="w-full py-3 rounded-xl text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors" data-id="element-276">
                Continue to Dashboard
              </button>
            </div>}

          {!checkedIn && <button onClick={onClose} className="w-full py-2 text-sm text-gray-400 hover:text-gray-600 transition-colors" data-id="element-277">
              Skip for now
            </button>}
        </div>
      </div>
    </div>;
}